import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAssistantEndpoints, SENDER_TYPE } from 'features/ai-assistant/constants';
import { useShowAlert } from 'hooks';
import LogRocket from 'logrocket';
import { ALT_API_CLIENT, getCancelToken } from 'services/API';
import strings from 'strings';
import { LogRocketCustomEvents } from 'UI/constants/LogRocketCustomEvents';
import { UIStatus } from 'UI/constants/status';
import { getId } from 'UI/utils';

const MESSAGES_PER_PAGE = 20;

export const useConversation = ({
  apiInstance = ALT_API_CLIENT,
  basePath,
  sessionId = null,
  initialSearchText,
  onMessageReplied,
  onMessagesLoaded
}) => {
  const { showAlert } = useShowAlert();

  const cancelTokenFetchMessages = useRef(null);
  const cancelTokenFetchMore = useRef(null);
  const cancelTokenSendMessage = useRef(null);
  const currentSessionId = useRef(null);

  const [messages, setMessages] = useState([]);
  const [uiStatus, setUiStatus] = useState(UIStatus.Default);
  const [scrollConfig, setScrollConfig] = useState({
    behavior: 'instant',
    shouldScroll: false
  });
  const [paginationState, setPaginationState] = useState({
    page: 1,
    hasMore: false,
    isLoadingMore: false
  });

  const responsesEndpoint = useMemo(() => getAssistantEndpoints(basePath).responses, [basePath]);

  const cancelIfExists = useCallback(cancelRef => {
    if (cancelRef.current) {
      cancelRef.current.cancel(strings.shared.requests.cancelTokenMessage);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    cancelIfExists(cancelTokenFetchMessages);
    cancelTokenFetchMessages.current = getCancelToken();

    try {
      if (!sessionId) return;

      setUiStatus(UIStatus.Loading);
      setMessages([]);

      const response = await apiInstance.get(responsesEndpoint, {
        cancelToken: cancelTokenFetchMessages.current.token,
        params: {
          page: 1,
          perPage: MESSAGES_PER_PAGE,
          sessionId
        }
      });

      const results = response.data?.data ?? [];

      const total = response.data?.total;
      const hasMore =
        typeof total === 'number' ? results.length < total : results.length < MESSAGES_PER_PAGE;

      setMessages(results);

      setUiStatus(UIStatus.Success);
      setPaginationState({
        page: 1,
        hasMore,
        isLoadingMore: false
      });
      setScrollConfig({
        behavior: 'instant',
        shouldScroll: true
      });
      onMessagesLoaded && onMessagesLoaded(results);
    } catch (error) {
      if (error?.message === strings.shared.requests.cancelTokenMessage) return;

      setUiStatus(UIStatus.Error);
      showAlert({
        title: strings.shared.ui.unknownError.title,
        error
      });
    }
  }, [apiInstance, cancelIfExists, onMessagesLoaded, responsesEndpoint, sessionId, showAlert]);

  useEffect(() => {
    cancelIfExists(cancelTokenFetchMessages);
    cancelIfExists(cancelTokenFetchMore);
    cancelIfExists(cancelTokenSendMessage);

    if (!sessionId) {
      currentSessionId.current = null;
      setMessages([]);
      setUiStatus(UIStatus.Default);
      setPaginationState({ page: 1, hasMore: false, isLoadingMore: false });
      return;
    }

    if (sessionId === currentSessionId.current) return;

    currentSessionId.current = sessionId;
    fetchMessages();
  }, [cancelIfExists, fetchMessages, sessionId]);

  useEffect(() => {
    return () => {
      cancelIfExists(cancelTokenFetchMessages);
      cancelIfExists(cancelTokenFetchMore);
      cancelIfExists(cancelTokenSendMessage);
    };
  }, [cancelIfExists]);

  const fetchMoreMessages = useCallback(async () => {
    const activeSessionId = currentSessionId.current ?? sessionId;

    if (!activeSessionId || !paginationState.hasMore || paginationState.isLoadingMore) return;

    try {
      setPaginationState(prev => ({ ...prev, isLoadingMore: true }));

      cancelIfExists(cancelTokenFetchMore);
      cancelTokenFetchMore.current = getCancelToken();

      const nextPage = paginationState.page + 1;

      const response = await apiInstance.get(responsesEndpoint, {
        cancelToken: cancelTokenFetchMore.current.token,
        params: {
          page: nextPage,
          perPage: MESSAGES_PER_PAGE,
          sessionId: activeSessionId
        }
      });

      if (activeSessionId !== currentSessionId.current) {
        setPaginationState(prev => ({ ...prev, isLoadingMore: false }));
        return;
      }

      const results = response.data?.data ?? [];
      const actualMessagesLength = messages.length + results.length;
      const total = response.data?.total ?? 0;

      setMessages(prev => [...prev, ...results]);
      setPaginationState(prev => ({
        ...prev,
        page: nextPage,
        hasMore: actualMessagesLength < total,
        isLoadingMore: false
      }));
      onMessagesLoaded && onMessagesLoaded(results);
    } catch (error) {
      if (error?.message === strings.shared.requests.cancelTokenMessage) return;
      setPaginationState(prev => ({ ...prev, isLoadingMore: false }));
    }
  }, [
    apiInstance,
    cancelIfExists,
    messages.length,
    onMessagesLoaded,
    paginationState,
    responsesEndpoint,
    sessionId
  ]);

  const handleClickSend = useCallback(
    async inputMessage => {
      const draftId = `draft-${getId()}`;

      try {
        setUiStatus(UIStatus.Sending);

        cancelIfExists(cancelTokenSendMessage);
        cancelTokenSendMessage.current = getCancelToken();

        setMessages(prev => [
          {
            id: draftId,
            isDraft: true,
            isLatestAssistant: true,
            senderType: SENDER_TYPE.assistant
          },
          {
            createdAt: getId(),
            message: inputMessage,
            senderType: SENDER_TYPE.user
          },
          ...(prev ?? []).map(m => ({ ...m, isLatestAssistant: false }))
        ]);

        setScrollConfig({
          behavior: 'smooth',
          shouldScroll: true
        });

        const response = await apiInstance.post(
          responsesEndpoint,
          { message: inputMessage, sessionId: currentSessionId.current },
          { cancelToken: cancelTokenSendMessage.current.token }
        );

        const assistantOutput = response.data?.output;
        const assistantMessage =
          typeof assistantOutput === 'string' && assistantOutput
            ? assistantOutput
            : strings.ai.generators.assistant.emptyConversation.emptyMessage;

        currentSessionId.current = response.data?.sessionId;

        setMessages(prev =>
          prev.map(m =>
            m.id === draftId
              ? {
                  ...response.data,
                  createdAt: response.data?.createdAt ?? new Date().toISOString(),
                  message: assistantMessage,
                  senderType: SENDER_TYPE.assistant,
                  isLatestAssistant: true
                }
              : m
          )
        );

        setUiStatus(UIStatus.Success);
        onMessageReplied && onMessageReplied(response.data);
        LogRocket.track(LogRocketCustomEvents.AI.TrainingAssistant.SendMessage);
      } catch (error) {
        if (error?.message === strings.shared.requests.cancelTokenMessage) return;

        setMessages(prev => prev.filter(m => m.id !== draftId));
        setUiStatus(UIStatus.Error);
        showAlert({
          title: strings.ai.generators.assistant.errors.sendMessage,
          error
        });

        LogRocket.captureException(error, {
          extra: {
            event: LogRocketCustomEvents.AI.TrainingAssistant.SendMessage
          }
        });
      }
    },
    [apiInstance, cancelIfExists, onMessageReplied, responsesEndpoint, showAlert]
  );

  const resetScrollConfig = useCallback(() => {
    setScrollConfig({
      behavior: 'instant',
      shouldScroll: false
    });
  }, []);

  const initialTextSent = useRef(false);

  useEffect(() => {
    if (!initialSearchText || initialTextSent.current) return;

    initialTextSent.current = true;
    handleClickSend(initialSearchText);
  }, [handleClickSend, initialSearchText]);

  return {
    fetchMoreMessages,
    handleClickSend,
    isLoadingMoreMessages: paginationState.isLoadingMore,
    messages,
    resetScrollConfig,
    scrollConfig,
    uiStatus
  };
};
